'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSSEEvent } from '@/components/providers/sse-provider'
import { getContainers, startContainerAction, stopContainerAction, removeContainerAction, bulkStopContainers, bulkRemoveContainers } from '@/app/actions/docker'
import { Spinner } from '@/components/ui/spinner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { ContainerLogs } from './container-logs'
import { DockerComposeViewer } from './docker-compose-viewer'
import type { ContainerInfo } from 'dockerode'
import {
  Play, CircleStop, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown,
  Loader2, AlertTriangle
} from 'lucide-react'

type SortField = 'name' | 'type' | 'state' | 'created' | 'image'
type SortDirection = 'asc' | 'desc'

interface ParsedContainer {
  id: string
  name: string
  displayName: string
  type: 'agent' | 'preview' | 'compose' | 'other'
  state: string
  status: string
  image: string
  created: number
  projectPath?: string
  configFiles?: string
  ports: string[]
  traefikHost?: string
  raw: ContainerInfo
}

function parseContainerName(name: string): { displayName: string; type: ParsedContainer['type'] } {
  // Remove leading slash
  const cleanName = name.replace(/^\//, '')

  // Agent containers: loop-{timestamp}-{chatName}-{randomId}
  const agentMatch = cleanName.match(/^loop-(\d+)-(.+)-([a-z0-9]+)$/)
  if (agentMatch) {
    const chatName = agentMatch[2]
    return { displayName: `Agent: ${chatName}`, type: 'agent' }
  }

  // Preview containers: preview-{id}
  if (cleanName.startsWith('preview-')) {
    const previewId = cleanName.replace('preview-', '')
    return { displayName: `Preview: ${previewId.slice(0, 12)}`, type: 'preview' }
  }

  // Docker compose containers usually have project name
  if (cleanName.includes('-') && !cleanName.startsWith('loop-')) {
    return { displayName: cleanName, type: 'compose' }
  }

  return { displayName: cleanName, type: 'other' }
}

function parseTraefikHost(labels: Record<string, string> | undefined): string | undefined {
  if (!labels) return undefined

  // Look for traefik.http.routers.*.rule=Host(`domain.com`)
  for (const [key, value] of Object.entries(labels)) {
    if (key.match(/^traefik\.http\.routers\..+\.rule$/)) {
      const hostMatch = value.match(/Host\(`([^`]+)`\)/)
      if (hostMatch) {
        return hostMatch[1]
      }
    }
  }
  return undefined
}

function parseContainer(container: ContainerInfo): ParsedContainer {
  const name = container.Names[0] || ''
  const { displayName, type } = parseContainerName(name)

  const ports = (container.Ports || [])
    .filter(p => p.PublicPort)
    .map(p => `${p.PublicPort}:${p.PrivatePort}/${p.Type}`)

  const traefikHost = parseTraefikHost(container.Labels)

  return {
    id: container.Id,
    name: name.replace(/^\//, ''),
    displayName,
    type,
    state: container.State || 'unknown',
    status: container.Status || '',
    image: container.Image || '',
    created: container.Created || 0,
    projectPath: container.Labels?.['com.docker.compose.project.working_dir'],
    configFiles: container.Labels?.['com.docker.compose.project.config_files'],
    ports,
    traefikHost,
    raw: container,
  }
}

function formatAge(timestamp: number): string {
  const now = Date.now() / 1000
  const diff = now - timestamp

  if (diff < 60) return `${Math.floor(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function getStateColor(state: string): string {
  switch (state) {
    case 'running': return 'bg-green-500'
    case 'exited': return 'bg-red-500'
    case 'paused': return 'bg-yellow-500'
    case 'restarting': return 'bg-blue-500'
    default: return 'bg-gray-500'
  }
}

function getTypeBadgeVariant(type: ParsedContainer['type']): 'default' | 'secondary' | 'outline' {
  switch (type) {
    case 'agent': return 'default'
    case 'preview': return 'secondary'
    default: return 'outline'
  }
}

export function ContainerTable() {
  const [containers, setContainers] = useState<ParsedContainer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showExited, setShowExited] = useState(false)
  const [sortField, setSortField] = useState<SortField>('state')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [bulkActionLoading, setBulkActionLoading] = useState<'stop' | 'remove' | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<{ type: 'stop' | 'remove'; ids: string[] } | null>(null)

  const fetchContainers = useCallback(async () => {
    const data = await getContainers() as ContainerInfo[]
    setContainers(data.map(parseContainer))
    setIsLoading(false)
  }, [])

  useEffect(() => {
    fetchContainers()
  }, [fetchContainers])

  // Listen for container updates
  useSSEEvent('container_update', () => {
    fetchContainers()
  }, [fetchContainers])

  // Filter and sort containers
  const filteredContainers = useMemo(() => {
    let result = containers

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase()
      result = result.filter(c =>
        c.name.toLowerCase().includes(searchLower) ||
        c.displayName.toLowerCase().includes(searchLower) ||
        c.image.toLowerCase().includes(searchLower)
      )
    }

    // Filter exited
    if (!showExited) {
      result = result.filter(c => c.state !== 'exited')
    }

    // Sort
    result = [...result].sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'name':
          comparison = a.displayName.localeCompare(b.displayName)
          break
        case 'type':
          comparison = a.type.localeCompare(b.type)
          break
        case 'state':
          // Custom order: running > paused > restarting > created > exited
          const stateOrder = { running: 0, paused: 1, restarting: 2, created: 3, exited: 4 }
          comparison = (stateOrder[a.state as keyof typeof stateOrder] ?? 5) -
                       (stateOrder[b.state as keyof typeof stateOrder] ?? 5)
          break
        case 'created':
          comparison = b.created - a.created
          break
        case 'image':
          comparison = a.image.localeCompare(b.image)
          break
      }
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [containers, search, showExited, sortField, sortDirection])

  // Selection helpers
  const allSelected = filteredContainers.length > 0 &&
    filteredContainers.every(c => selected.has(c.id))
  const someSelected = selected.size > 0

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(filteredContainers.map(c => c.id)))
    }
  }

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selected)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelected(newSelected)
  }

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />
  }

  // Actions
  const handleStart = async (id: string) => {
    setActionLoading(id)
    try {
      await startContainerAction(id)
    } catch (error) {
      console.error('Start failed:', error)
    }
    setActionLoading(null)
  }

  const handleStop = async (id: string) => {
    setActionLoading(id)
    try {
      await stopContainerAction(id)
    } catch (error) {
      console.error('Stop failed:', error)
    }
    setActionLoading(null)
  }

  const handleRemove = async (id: string) => {
    setConfirmDialog({ type: 'remove', ids: [id] })
  }

  const handleBulkStop = () => {
    const ids = Array.from(selected).filter(id => {
      const c = containers.find(c => c.id === id)
      return c && c.state === 'running'
    })
    if (ids.length > 0) {
      setConfirmDialog({ type: 'stop', ids })
    }
  }

  const handleBulkRemove = () => {
    const ids = Array.from(selected)
    if (ids.length > 0) {
      setConfirmDialog({ type: 'remove', ids })
    }
  }

  const executeConfirmedAction = async () => {
    if (!confirmDialog) return

    const { type, ids } = confirmDialog
    setConfirmDialog(null)

    if (ids.length === 1) {
      setActionLoading(ids[0])
      try {
        if (type === 'stop') {
          await stopContainerAction(ids[0])
        } else {
          await removeContainerAction(ids[0])
        }
      } catch (error) {
        console.error(`${type} failed:`, error)
      }
      setActionLoading(null)
    } else {
      setBulkActionLoading(type)
      try {
        if (type === 'stop') {
          await bulkStopContainers(ids)
        } else {
          await bulkRemoveContainers(ids)
        }
        setSelected(new Set())
      } catch (error) {
        console.error(`Bulk ${type} failed:`, error)
      }
      setBulkActionLoading(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    )
  }

  const selectedCount = selected.size
  const stoppableSelectedCount = Array.from(selected).filter(id => {
    const c = containers.find(c => c.id === id)
    return c && (c.state === 'running' || c.state === 'restarting')
  }).length

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search containers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-full sm:w-64"
            />
          </div>
          <label className="flex items-center gap-2 text-sm whitespace-nowrap">
            <Checkbox
              checked={showExited}
              onCheckedChange={(checked) => setShowExited(checked === true)}
            />
            Show exited
          </label>
        </div>

        {/* Bulk actions */}
        {someSelected && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{selectedCount} selected</span>
            {stoppableSelectedCount > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleBulkStop}
                disabled={bulkActionLoading !== null}
              >
                {bulkActionLoading === 'stop' ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <CircleStop className="h-4 w-4 mr-1" />
                )}
                Stop ({stoppableSelectedCount})
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={handleBulkRemove}
              disabled={bulkActionLoading !== null}
            >
              {bulkActionLoading === 'remove' ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
              Remove ({selectedCount})
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      {filteredContainers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {containers.length === 0
            ? 'No containers found'
            : search
              ? 'No containers match your search'
              : 'No running containers (enable "Show exited" to see stopped containers)'
          }
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>
                  <button
                    className="flex items-center hover:text-foreground"
                    onClick={() => handleSort('name')}
                  >
                    Name <SortIcon field="name" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    className="flex items-center hover:text-foreground"
                    onClick={() => handleSort('type')}
                  >
                    Type <SortIcon field="type" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    className="flex items-center hover:text-foreground"
                    onClick={() => handleSort('state')}
                  >
                    Status <SortIcon field="state" />
                  </button>
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  <button
                    className="flex items-center hover:text-foreground"
                    onClick={() => handleSort('image')}
                  >
                    Image <SortIcon field="image" />
                  </button>
                </TableHead>
                <TableHead className="hidden md:table-cell">URL / Ports</TableHead>
                <TableHead>
                  <button
                    className="flex items-center hover:text-foreground"
                    onClick={() => handleSort('created')}
                  >
                    Age <SortIcon field="created" />
                  </button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContainers.map((container) => {
                const isRunning = container.state === 'running'
                const canStop = container.state === 'running' || container.state === 'restarting'
                const isSelected = selected.has(container.id)
                const isActionLoading = actionLoading === container.id

                return (
                  <TableRow
                    key={container.id}
                    data-state={isSelected ? 'selected' : undefined}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(container.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium truncate max-w-[200px]" title={container.name}>
                          {container.displayName}
                        </span>
                        {container.projectPath && (
                          <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={container.projectPath}>
                            {container.projectPath.split('/').slice(-2).join('/')}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getTypeBadgeVariant(container.type)}>
                        {container.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`${getStateColor(container.state)} text-white border-0`}
                        >
                          {container.state}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground truncate max-w-[200px] block" title={container.image}>
                        {container.image.startsWith('sha256:')
                          ? container.image.slice(7, 19)
                          : container.image.split('/').pop()?.split(':')[0] || container.image
                        }
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {container.traefikHost ? (
                        <a
                          href={`https://${container.traefikHost}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:underline"
                        >
                          {container.traefikHost}
                        </a>
                      ) : container.ports.length > 0 ? (
                        <span className="text-xs text-muted-foreground">
                          {container.ports.slice(0, 2).join(', ')}
                          {container.ports.length > 2 && ` +${container.ports.length - 2}`}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {formatAge(container.created)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {canStop ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleStop(container.id)}
                            disabled={isActionLoading}
                            title={container.state === 'restarting' ? 'Stop (restarting)' : 'Stop'}
                          >
                            {isActionLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <CircleStop className="h-4 w-4" />
                            )}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleStart(container.id)}
                            disabled={isActionLoading}
                            title="Start"
                          >
                            {isActionLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <ContainerLogs
                          containerId={container.id}
                          containerName={container.name}
                        />
                        {container.configFiles && (
                          <DockerComposeViewer
                            configPath={container.configFiles.split(',')[0]}
                          />
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemove(container.id)}
                          disabled={isActionLoading || canStop}
                          title={canStop ? 'Stop container first' : 'Remove'}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog !== null} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirm {confirmDialog?.type === 'stop' ? 'Stop' : 'Remove'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.ids.length === 1
                ? `Are you sure you want to ${confirmDialog?.type} this container?`
                : `Are you sure you want to ${confirmDialog?.type} ${confirmDialog?.ids.length} containers?`
              }
              {confirmDialog?.type === 'remove' && (
                <span className="block mt-2 text-destructive">
                  This action cannot be undone.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>
              Cancel
            </Button>
            <Button
              variant={confirmDialog?.type === 'remove' ? 'destructive' : 'default'}
              onClick={executeConfirmedAction}
            >
              {confirmDialog?.type === 'stop' ? 'Stop' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
